import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
export function LiabilitiesManager({ currency, onChange }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", kind: "credit_card", balance: "", interest_rate: "", currency: currency || "USD" });

  const load = async () => {
    const { data } = await api.get("/liabilities");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/liabilities", {
        name: form.name.trim(),
        kind: form.kind,
        balance: parseFloat(form.balance),
        interest_rate: form.interest_rate === "" ? null : parseFloat(form.interest_rate),
        currency: form.currency,
      });
      setForm({ name: "", kind: "credit_card", balance: "", interest_rate: "", currency: currency || "USD" });
      setOpen(false);
      await load();
      onChange && onChange();
      toast.success("Liability added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/liabilities/${id}`);
      await load();
      onChange && onChange();
      toast.success("Removed");
    } catch (err) {
      toast.error("Could not remove");
    }
  };

  return (
    <div data-testid="liabilities-manager" className="card-flat overflow-hidden">
      <div className="px-5 py-3 border-b border-[var(--divider)] flex items-center justify-between">
        <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Liabilities</div>
        <Button size="sm" variant="outline" data-testid="add-liability-btn" onClick={() => setOpen(true)} className="h-8 gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Add
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="p-5 text-sm text-[var(--muted-text)]">No liabilities tracked. Add credit cards, loans, or anything you owe to keep the balance sheet complete.</div>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {items.map((l) => (
              <tr key={l.id} data-testid={`liability-row-${l.name}`} className="border-b border-[var(--divider)] last:border-none">
                <td className="py-2.5 px-5">
                  <div className="flex items-center gap-2">
                    {l.name}
                    <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-[var(--hover)] text-[var(--muted-text)]">{l.currency || "USD"}</span>
                  </div>
                  <div className="text-xs text-[var(--muted-text)] capitalize">{l.kind.replace("_", " ")}{l.interest_rate != null && ` · ${l.interest_rate}% APR`}</div>
                </td>
                <td className="py-2.5 px-5 text-right font-mono-data">{formatCurrency(l.balance, l.currency || "USD")}</td>
                <td className="py-2.5 px-5 w-10 text-right">
                  <Button size="icon" variant="ghost" data-testid={`liability-delete-${l.name}`} onClick={() => remove(l.id)} aria-label="Delete">
                    <Trash2 className="w-4 h-4 text-[#E63946]" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add a liability</DialogTitle>
            <DialogDescription>Track credit cards, loans, or anything you owe so your balance sheet stays complete. Pick the currency the debt is denominated in — NGN loans land on the NGN rail automatically.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input data-testid="liab-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Chase Sapphire, Car Loan" required maxLength={60} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Kind</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="liab-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit_card">Credit card</SelectItem>
                    <SelectItem value="loan">Loan</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="liab-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="NGN">NGN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>APR (optional)</Label>
                <Input type="number" step="0.01" value={form.interest_rate} onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} placeholder="e.g. 18.9" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Balance owed</Label>
              <Input data-testid="liab-balance" type="number" step="0.01" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} required />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="liab-submit" type="submit" disabled={!form.name.trim() || !form.balance} className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]">
                Add liability
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
